import { useEffect, useState } from "react";
import { EyeOff } from "lucide-react";
import { Loading, ErrorBox, KPI, Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useI18n, FAMILIES, BANDS, STATUS } from "@/lib/i18n";

const Suppressed = ({ t }) => <span className="inline-flex items-center gap-1 text-xs text-slate-500"><EyeOff className="h-3.5 w-3.5" />{t}</span>;

function Question({ q, threshold }) {
  const { lang, tx, lbl } = useI18n();
  const hidden = tx(`masqué (n < ${threshold})`, `suppressed (n < ${threshold})`);
  const max = q.distribution ? Math.max(1, ...Object.values(q.distribution)) : 1;
  return (
    <div className="border-b border-slate-100 py-4" data-testid={`analytics-q-${q.qid}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div className="text-sm"><span className="font-mono text-xs text-slate-500 mr-2">{q.qid}</span>{lang === "fr" ? q.text_fr : q.text_en}</div>
        <div className="text-xs font-mono text-slate-600" data-testid={`analytics-n-${q.qid}`}>n = {q.n} / {q.denominator} · NA = {q.na}{q.mean != null && ` · ${tx("moy.", "mean")} ${q.mean} · ${tx("méd.", "median")} ${q.median}`}</div>
      </div>
      {q.suppressed ? <div className="mt-2"><Suppressed t={hidden} /></div> : (
        <div className="grid md:grid-cols-[1fr_1fr] gap-6 mt-3">
          <div className="space-y-1">{Object.entries(q.distribution).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-xs"><span className="w-10 font-mono">{k}</span><div className="flex-1 h-3 bg-slate-100 rounded"><div className="h-3 rounded bg-[#2176AE]" style={{ width: `${(100 * v) / max}%` }} /></div><span className="w-6 text-right font-mono">{v}</span></div>
          ))}</div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {[["by_unit", tx("Unité", "Unit"), null], ["by_tenure_band", tx("Ancienneté", "Tenure"), BANDS]].map(([k, title, map]) => (
              <div key={k}><div className="label-xs mb-1">{title}</div>{q[k].map((g) => (
                <div key={g.group} className="flex justify-between py-0.5"><span className="truncate">{map ? lbl(map, g.group) : g.group}</span>{g.suppressed ? <Suppressed t={`n<${threshold}`} /> : <span className="font-mono">n={g.n}{g.mean != null && ` · ${g.mean}`}</span>}</div>
              ))}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Analytics({ audit }) {
  const { tx, lbl } = useI18n();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/audits/${audit.id}/analytics`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [audit.id]);
  if (err) return <ErrorBox error={err} />;
  if (!d) return <Loading />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI testid="analytics-population" label={tx("Effectif actif", "Active population")} value={d.population} />
        <KPI testid="analytics-sampled" label={tx("Entretiens planifiés", "Planned interviews")} value={d.sampled} />
        <KPI testid="analytics-eligible" label={tx("Entretiens admissibles", "Eligible interviews")} value={d.eligible_interviews} sub={d.eligible_statuses.map((s) => lbl(STATUS, s)).join(", ")} />
        <KPI testid="analytics-coverage" label={tx("Couverture / effectif", "Coverage / population")} value={`${d.coverage_pct_population}%`} sub={`${tx("Seuil petits groupes", "Small-cell threshold")}: ${d.threshold}`} />
      </div>
      <p className="text-xs text-slate-600 rounded border border-slate-200 bg-white px-3 py-2" data-testid="analytics-disclaimer">{tx("Statistiques descriptives sur les seuls entretiens admissibles. Aucune causalité ni représentativité n'est impliquée ; les résultats dépendent du protocole d'échantillonnage.", d.disclaimer)}</p>
      {d.families.length === 0 && <div className="card-surface"><Empty text={tx("Aucun entretien admissible", "No eligible interviews")} /></div>}
      {d.families.map((f) => (
        <section key={f.family} className="card-surface p-4" data-testid={`analytics-family-${f.family}`}>
          <div className="flex justify-between items-baseline"><div className="font-display font-semibold text-[#153A63]">{lbl(FAMILIES, f.family)}</div><span className="font-mono text-xs">n = {f.n_interviews}</span></div>
          {f.suppressed ? <div className="mt-3"><Suppressed t={tx(`Groupe masqué : moins de ${d.threshold} entretiens admissibles`, `Group suppressed: fewer than ${d.threshold} eligible interviews`)} /></div> : f.questions.map((q) => <Question key={q.qid} q={q} threshold={d.threshold} />)}
        </section>
      ))}
      <p className="text-xs text-slate-500">{tx("Thèmes qualitatifs et tendances temporelles : prévus au jalon 3.", "Qualitative themes and time trends: planned for milestone 3.")}</p>
    </div>
  );
}
